/**
 * HTTP 客户端（spec 06 §1、08 §2 "原生 fetch 薄封装，不引 axios"）。
 *
 * 这一层负责、也只负责六件事：
 *   1. 鉴权头 `Authorization: Bearer <token>`（spec 00 §6 偏离 3）
 *   2. 写操作自动带 `Idempotency-Key`（spec 06 §1.2）
 *   3. 错误归一成 ApiError，页面按 `code` 分支，绝不解析 message（spec 08 §10.4）
 *   4. 超时 + 取消（spec 00 §3.3-17）
 *   5. 只对**幂等且瞬态**的失败做有限退避重试
 *   6. 业务接口 401 单飞刷新，刷新失败才踢登录；登录握手自身的 401 直接抛，绝不续期
 *
 * 不负责：缓存、去重、轮询——那是 TanStack Query 的活。
 */

import { describeError, errorText } from '@/lib/errors';

/** 契约里的游标分页信封（spec 06 §1.1）。 */
export interface CursorPage<T> {
  items: T[];
  next_cursor: string | null;
  has_more: boolean;
}

/** 契约里的错误信封（spec 06 §1.1）。 */
interface ErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    request_id?: string;
    details?: unknown;
  };
}

export class ApiError extends Error {
  readonly code: string;
  readonly httpStatus: number;
  readonly requestId: string | undefined;
  readonly details: unknown;
  /** 服务端原话。`message` 已经过 `lib/errors` 翻成人话，排障时看这个。 */
  readonly serverMessage: string;

  constructor(init: {
    code: string;
    message: string;
    httpStatus: number;
    requestId?: string | undefined;
    details?: unknown;
  }) {
    // 统一走错误码 → 人话映射（总纲 §7-4、§9）：工程师话换人话，未知码带 request_id
    super(errorText(init));
    this.name = 'ApiError';
    this.code = init.code;
    this.httpStatus = init.httpStatus;
    this.requestId = init.requestId;
    this.details = init.details;
    this.serverMessage = init.message;
  }

  /** 给人看的完整形态：title / 下一步 / 去处理的链接 / 能不能重试 / request_id。 */
  get human() {
    return describeError({
      code: this.code,
      message: this.serverMessage,
      httpStatus: this.httpStatus,
      requestId: this.requestId,
      details: this.details,
    });
  }

  /** 网络断了 / 超时 / DNS 挂了这类「没走到服务端」的失败。 */
  get isNetwork(): boolean {
    return this.httpStatus === 0;
  }

  /** 可安全重试的瞬态失败。写操作即便命中也不自动重试，交给用户重新点。 */
  get isTransient(): boolean {
    return this.isNetwork || this.httpStatus === 429 || this.httpStatus >= 500;
  }
}

/* ========== 鉴权接线（避免 client ↔ store 循环依赖） ========== */

interface AuthBridge {
  getAccessToken: () => string | null;
  /** 返回新 access token；返回 null 表示刷新失败。实现方必须自己做单飞。 */
  refresh: () => Promise<string | null>;
  /** 清登录态回登录页。reason 是给人看的原因（被停用），普通的会话过期不传。 */
  onUnauthorized: (reason?: string) => void;
}

let auth: AuthBridge = {
  getAccessToken: () => null,
  refresh: () => Promise.resolve(null),
  onUnauthorized: () => {},
};

export function configureAuth(bridge: AuthBridge): void {
  auth = bridge;
}

/* ========== 配置 ========== */

/** 控制台面（JWT）。spec 01 §2.1 的四个挂载面里，前端只用到这两个。 */
const BASE_URL = import.meta.env['VITE_API_BASE'] ?? '/console/v1';
/** 匿名面（货架、试打、OEM 渠道、分享页）。鉴权策略与限流粒度都和控制台面不同。 */
export const PUBLIC_BASE = '/public/v1';
const DEFAULT_TIMEOUT_MS = 20_000;
/** 只重试幂等请求，且次数有界（spec 00 §3.3-17）。 */
const MAX_RETRY = 2;
const RETRY_BASE_DELAY_MS = 300;

type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  query?: Query;
  body?: unknown;
  signal?: AbortSignal;
  timeoutMs?: number;
  /** 写操作的幂等键。不传则自动生成（spec 06 §1.2：创建类必须带）。 */
  idempotencyKey?: string;
  /** 覆盖默认重试判定。GET 默认可重试，写操作默认不重试。 */
  retry?: boolean;
  /** 换挂载面。缺省走控制台面，匿名面传 PUBLIC_BASE。 */
  base?: string;
  /**
   * 401 时是否先单飞刷票再重放。默认：业务接口开、登录握手关。
   * `/auth/refresh` 自己 401 再刷，会等自己的 in-flight Promise，按钮永远转圈。
   */
  refreshOn401?: boolean;
}

/** 登录握手自己的 401 是「账密不对 / 票作废」，不是「会话过期该续期」。 */
function isAuthHandshake(path: string): boolean {
  return (
    path.startsWith('/auth/login') ||
    path.startsWith('/auth/register') ||
    path === '/auth/refresh' ||
    path === '/auth/logout' ||
    path === '/auth/sms/send'
  );
}

function buildUrl(path: string, query?: Query, base?: string): string {
  const url = `${base ?? BASE_URL}${path}`;
  if (!query) return url;
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === null || v === undefined || v === '') continue;
    sp.append(k, String(v));
  }
  const qs = sp.toString();
  return qs ? `${url}?${qs}` : url;
}

function newId(): string {
  return crypto.randomUUID();
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(t);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}

async function toApiError(res: Response): Promise<ApiError> {
  const requestId = res.headers.get('X-Request-Id') ?? undefined;
  let code = `http_${res.status}`;
  // 没拿到信封就留空，让 lib/errors 按状态段给人话（网关 502 之类）
  let message = '';
  let details: unknown;

  try {
    const body = (await res.json()) as ErrorEnvelope;
    if (body.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      details = body.error.details;
    }
  } catch {
    // 非 JSON 响应（网关 502 之类），保留兜底文案
  }

  return new ApiError({
    code,
    message,
    httpStatus: res.status,
    requestId: requestId ?? undefined,
    details,
  });
}

async function once<T>(
  method: string,
  path: string,
  opts: RequestOptions,
  idempotencyKey: string | undefined,
  allowRefresh: boolean,
): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), opts.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  opts.signal?.addEventListener('abort', () => controller.abort(), { once: true });

  const headers = new Headers({ 'X-Request-Id': newId() });
  const token = auth.getAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (opts.body !== undefined) headers.set('Content-Type', 'application/json');
  if (idempotencyKey) headers.set('Idempotency-Key', idempotencyKey);

  let res: Response;
  try {
    res = await fetch(buildUrl(path, opts.query, opts.base), {
      method,
      headers,
      body: opts.body === undefined ? null : JSON.stringify(opts.body),
      signal: controller.signal,
      credentials: 'same-origin',
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiError({
      code: 'network_error',
      message: '网络不可用，请检查连接后重试。',
      httpStatus: 0,
    });
  } finally {
    clearTimeout(timeout);
  }

  if (res.status === 401 && allowRefresh) {
    const fresh = await auth.refresh();
    if (fresh) return once<T>(method, path, opts, idempotencyKey, false);
    auth.onUnauthorized();
  }

  if (!res.ok) {
    const err = await toApiError(res);
    // 被本企业停用：服务端先判停用再判版本号，之后每个请求都是 403 membership_disabled，
    // 永远等不到 401，不主动清登录态的话人会一直卡在报错的页面上。
    // 切换企业时的 membership_disabled 说的是「目标企业进不去」，当前会话仍有效，不踢。
    if (err.code === 'membership_disabled' && allowRefresh && path !== '/auth/switch-tenant') {
      auth.onUnauthorized(err.message);
    }
    throw err;
  }
  if (res.status === 204) return undefined as T;

  return (await res.json()) as T;
}

async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const isWrite = method !== 'GET' && method !== 'HEAD';
  // 幂等键在**重试之间保持不变**，否则重发会被当成一笔新业务（spec 06 §1.2）
  const idempotencyKey = isWrite ? (opts.idempotencyKey ?? newId()) : undefined;
  const retryable = opts.retry ?? !isWrite;

  let lastError: unknown;
  const allowRefresh = opts.refreshOn401 ?? !isAuthHandshake(path);
  for (let attempt = 0; attempt <= (retryable ? MAX_RETRY : 0); attempt++) {
    try {
      return await once<T>(method, path, opts, idempotencyKey, allowRefresh);
    } catch (e) {
      lastError = e;
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      const canRetry = retryable && e instanceof ApiError && e.isTransient;
      if (!canRetry || attempt === MAX_RETRY) throw e;
      // 指数退避 + 抖动，避免整页 12 个请求同时重试把服务端再打一遍
      const delay = RETRY_BASE_DELAY_MS * 2 ** attempt * (0.5 + Math.random());
      await sleep(delay, opts.signal);
    }
  }
  throw lastError;
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, opts),
  post: <T>(path: string, opts?: RequestOptions) => request<T>('POST', path, opts),
  put: <T>(path: string, opts?: RequestOptions) => request<T>('PUT', path, opts),
  patch: <T>(path: string, opts?: RequestOptions) => request<T>('PATCH', path, opts),
  del: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, opts),
};

/** 列表接口的 limit 上限（spec 06 §1.1）。 */
export const PAGE_LIMIT = 20;
export const PAGE_LIMIT_MAX = 200;
